import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/**
 * PAAT profily — `/admin/api/v1/paat-profiles` (API chain, JWT cookie, CSRF off).
 * Device-info profily pro eChannels aktivaci. Plný CRUD + set-default +
 * bulk-update verzí dle platformy.
 */

export interface PaatProfile {
  id: string;
  profileName: string;
  isDefault: boolean;
  versionCode: string | null;
  appVersion: string | null;
  sdkVersion: string | null;
  apiLevel: string | null;
  language: string | null;
  platform: string; // PlatformType enum name
  deviceModel: string | null;
  deviceName: string | null;
  deviceManufacturer: string | null;
  usageCount: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface PaatPlatform {
  value: string;
  label: string;
}

/** Pole zapisovaná při create/update (create navíc `id`). */
export interface PaatProfileBody {
  profileName: string;
  versionCode?: string | null;
  appVersion: string;
  sdkVersion: string;
  apiLevel: string;
  language: string;
  platform: string;
  deviceModel: string;
  deviceName: string;
  deviceManufacturer: string;
}

export interface BulkUpdateBody {
  platform: string;
  versionCode?: string | null;
  appVersion?: string | null;
  sdkVersion?: string | null;
}

const KEY = ["paat-profiles"];

export function usePaatProfilesList() {
  return useQuery({ queryKey: KEY, queryFn: () => apiFetch<PaatProfile[]>("/admin/api/v1/paat-profiles") });
}

export function usePaatPlatforms() {
  return useQuery({
    queryKey: ["paat-platforms"],
    queryFn: () => apiFetch<PaatPlatform[]>("/admin/api/v1/paat-profiles/platforms"),
  });
}

export function useCreatePaatProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PaatProfileBody & { id: string }) =>
      apiFetch<PaatProfile>("/admin/api/v1/paat-profiles", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdatePaatProfile(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PaatProfileBody) =>
      apiFetch<PaatProfile>(`/admin/api/v1/paat-profiles/${encodeURIComponent(id)}`, { method: "PUT", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeletePaatProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/admin/api/v1/paat-profiles/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSetDefaultPaatProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/admin/api/v1/paat-profiles/${encodeURIComponent(id)}/set-default`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useBulkUpdatePaatVersions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkUpdateBody) =>
      apiFetch<{ message: string; updatedCount: number }>("/admin/api/v1/paat-profiles/bulk-update-versions", {
        method: "PATCH",
        json: body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
